import { useCallback, useMemo, useState } from 'react'
import type { components } from '../../api/generated/schema'
import type { Notice } from '../../lib/envelope'
import { describeApiFailure } from '../../lib/failure'
import { campaignApi, useCampaignMutation } from './mutations'

type Schema = components['schemas']

/**
 * Who may write what, as the server reported it.
 *
 * Campaign access is genuinely two-tiered, unlike a character sheet's single
 * `isOwner`: the Game Master writes the campaign and its world, while any member
 * keeps their own notes, and adding a character sits between the two under a rule
 * (`core.canAddCharacter`) the server evaluates per caller. Passing all three around
 * as one object is what keeps the cards from re-deriving any of it.
 */
export interface CampaignAccess {
  /** Renames, membership, roster removal, and every city/faction/location write. */
  isGameMaster: boolean
  /** GM, player or admin. Enough to read, and to keep private notes. */
  isMember: boolean
  /** `core.canAddCharacter` — server-computed, never re-decided here. */
  canAddCharacter: boolean
}

/**
 * Every campaign mutation, in one place.
 *
 * The same bundling as `useCharacterEditing`, and for the same reason: each mutation
 * reports through one pair of callbacks, so the page renders notices and failures once
 * rather than once per card.
 */
export function useCampaignEditing(campaignId: number, access: CampaignAccess) {
  const [notices, setNotices] = useState<Notice[]>([])
  const [failure, setFailure] = useState<string | null>(null)

  const onEnvelope = useCallback((envelope: { notices: Notice[] }) => {
    setNotices(envelope.notices)
    setFailure(null)
  }, [])

  const onFailure = useCallback((error: unknown) => {
    setNotices([])
    setFailure(describeApiFailure(error, 'Only the Game Master can change this campaign.'))
  }, [])

  const base = useMemo(
    () => ({ campaignId, onEnvelope, onFailure }),
    [campaignId, onEnvelope, onFailure],
  )

  // ----- campaign -----------------------------------------------------------

  const rename = useCampaignMutation<{ name: string }>({
    ...base,
    request: ({ name }) => campaignApi.rename(campaignId, name),
  })

  // ----- membership ---------------------------------------------------------

  const addPlayer = useCampaignMutation<{ userId: string }>({
    ...base,
    request: ({ userId }) => campaignApi.addPlayer(campaignId, userId),
  })

  const removePlayer = useCampaignMutation<{ userId: string }>({
    ...base,
    request: ({ userId }) => campaignApi.removePlayer(campaignId, userId),
  })

  // ----- roster -------------------------------------------------------------

  const addCharacter = useCampaignMutation<{ characterId: number }>({
    ...base,
    request: ({ characterId }) => campaignApi.addCharacter(campaignId, characterId),
  })

  const removeCharacter = useCampaignMutation<{ characterId: number }>({
    ...base,
    request: ({ characterId }) => campaignApi.removeCharacter(campaignId, characterId),
  })

  // ----- notes --------------------------------------------------------------

  const addNote = useCampaignMutation<Schema['NoteRequest']>({
    ...base,
    request: (body) => campaignApi.addNote(campaignId, body),
  })

  const updateNote = useCampaignMutation<{ noteId: number; body: Schema['NoteRequest'] }>({
    ...base,
    request: ({ noteId, body }) => campaignApi.updateNote(campaignId, noteId, body),
  })

  const removeNote = useCampaignMutation<{ noteId: number }>({
    ...base,
    request: ({ noteId }) => campaignApi.removeNote(campaignId, noteId),
  })

  // ----- city ---------------------------------------------------------------

  const createCity = useCampaignMutation<Schema['CityRequest']>({
    ...base,
    request: (body) => campaignApi.createCity(campaignId, body),
  })

  const updateCity = useCampaignMutation<Schema['CityRequest']>({
    ...base,
    request: (body) => campaignApi.updateCity(campaignId, body),
  })

  const removeCity = useCampaignMutation<void>({
    ...base,
    request: () => campaignApi.removeCity(campaignId),
  })

  // ----- factions -----------------------------------------------------------

  const addFaction = useCampaignMutation<Schema['FactionRequest']>({
    ...base,
    request: (body) => campaignApi.addFaction(campaignId, body),
  })

  const updateFaction = useCampaignMutation<{
    factionId: number
    body: Schema['FactionRequest']
  }>({
    ...base,
    request: ({ factionId, body }) => campaignApi.updateFaction(campaignId, factionId, body),
  })

  const removeFaction = useCampaignMutation<{ factionId: number }>({
    ...base,
    request: ({ factionId }) => campaignApi.removeFaction(campaignId, factionId),
  })

  /**
   * Edges are stored one-directional and the API keeps them that way, so "Allied with"
   * pointing back is a second call. The UI says so rather than implying symmetry.
   */
  const addRelationship = useCampaignMutation<{
    factionId: number
    body: Schema['FactionRelationshipRequest']
  }>({
    ...base,
    request: ({ factionId, body }) => campaignApi.addRelationship(campaignId, factionId, body),
  })

  const updateRelationship = useCampaignMutation<{
    relationshipId: number
    body: Schema['UpdateFactionRelationshipRequest']
  }>({
    ...base,
    request: ({ relationshipId, body }) =>
      campaignApi.updateRelationship(campaignId, relationshipId, body),
  })

  const removeRelationship = useCampaignMutation<{ relationshipId: number }>({
    ...base,
    request: ({ relationshipId }) => campaignApi.removeRelationship(campaignId, relationshipId),
  })

  /** The character must already be on the campaign roster — factions describe who is in play. */
  const addMember = useCampaignMutation<{
    factionId: number
    body: Schema['FactionMemberRequest']
  }>({
    ...base,
    request: ({ factionId, body }) => campaignApi.addMember(campaignId, factionId, body),
  })

  const updateMember = useCampaignMutation<{
    memberId: number
    body: Schema['UpdateFactionMemberRequest']
  }>({
    ...base,
    request: ({ memberId, body }) => campaignApi.updateMember(campaignId, memberId, body),
  })

  const removeMember = useCampaignMutation<{ memberId: number }>({
    ...base,
    request: ({ memberId }) => campaignApi.removeMember(campaignId, memberId),
  })

  // ----- locations ----------------------------------------------------------

  const addLocation = useCampaignMutation<Schema['LocationRequest']>({
    ...base,
    request: (body) => campaignApi.addLocation(campaignId, body),
  })

  const updateLocation = useCampaignMutation<{
    locationId: number
    body: Schema['LocationRequest']
  }>({
    ...base,
    request: ({ locationId, body }) => campaignApi.updateLocation(campaignId, locationId, body),
  })

  const removeLocation = useCampaignMutation<{ locationId: number }>({
    ...base,
    request: ({ locationId }) => campaignApi.removeLocation(campaignId, locationId),
  })

  const addLocationCharacter = useCampaignMutation<{
    locationId: number
    body: Schema['LocationCharacterRequest']
  }>({
    ...base,
    request: ({ locationId, body }) =>
      campaignApi.addLocationCharacter(campaignId, locationId, body),
  })

  const updateLocationCharacter = useCampaignMutation<{
    locationCharacterId: number
    body: Schema['UpdateLocationCharacterRequest']
  }>({
    ...base,
    request: ({ locationCharacterId, body }) =>
      campaignApi.updateLocationCharacter(campaignId, locationCharacterId, body),
  })

  const removeLocationCharacter = useCampaignMutation<{ locationCharacterId: number }>({
    ...base,
    request: ({ locationCharacterId }) =>
      campaignApi.removeLocationCharacter(campaignId, locationCharacterId),
  })

  const addLocationEncounter = useCampaignMutation<{
    locationId: number
    body: Schema['LocationEncounterRequest']
  }>({
    ...base,
    request: ({ locationId, body }) =>
      campaignApi.addLocationEncounter(campaignId, locationId, body),
  })

  const updateLocationEncounter = useCampaignMutation<{
    encounterId: number
    body: Schema['LocationEncounterRequest']
  }>({
    ...base,
    request: ({ encounterId, body }) =>
      campaignApi.updateLocationEncounter(campaignId, encounterId, body),
  })

  const removeLocationEncounter = useCampaignMutation<{ encounterId: number }>({
    ...base,
    request: ({ encounterId }) => campaignApi.removeLocationEncounter(campaignId, encounterId),
  })

  const all = [
    rename,
    addPlayer,
    removePlayer,
    addCharacter,
    removeCharacter,
    addNote,
    updateNote,
    removeNote,
    createCity,
    updateCity,
    removeCity,
    addFaction,
    updateFaction,
    removeFaction,
    addRelationship,
    updateRelationship,
    removeRelationship,
    addMember,
    updateMember,
    removeMember,
    addLocation,
    updateLocation,
    removeLocation,
    addLocationCharacter,
    updateLocationCharacter,
    removeLocationCharacter,
    addLocationEncounter,
    updateLocationEncounter,
    removeLocationEncounter,
  ]

  return {
    access,
    notices,
    failure,
    dismiss: useCallback(() => {
      setNotices([])
      setFailure(null)
    }, []),
    /** True while any campaign mutation is in flight. Nothing here is optimistic (mutations.ts). */
    isPending: all.some((mutation) => mutation.isPending),
    rename,
    addPlayer,
    removePlayer,
    addCharacter,
    removeCharacter,
    addNote,
    updateNote,
    removeNote,
    createCity,
    updateCity,
    removeCity,
    addFaction,
    updateFaction,
    removeFaction,
    addRelationship,
    updateRelationship,
    removeRelationship,
    addMember,
    updateMember,
    removeMember,
    addLocation,
    updateLocation,
    removeLocation,
    addLocationCharacter,
    updateLocationCharacter,
    removeLocationCharacter,
    addLocationEncounter,
    updateLocationEncounter,
    removeLocationEncounter,
  }
}

export type CampaignEditing = ReturnType<typeof useCampaignEditing>
